import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../contexts/AuthContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';

const { FiUsers, FiPlus, FiSettings, FiShield, FiMail, FiCrown, FiLock, FiZap, FiStar } = FiIcons;

const TeamManagement = () => {
  const { user } = useAuth();
  const { currentWorkspace, workspaceMembers, pendingInvites, inviteTeamMember, updateMemberRole, removeMember, cancelInvite, resendInvite } = useWorkspace();
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');

  const handleInvite = async () => {
    if (inviteEmail.trim()) {
      try {
        await inviteTeamMember({
          email: inviteEmail,
          role: 'editor',
          message: 'Join our workspace!'
        });
        setInviteEmail('');
        setShowInviteModal(false);
      } catch (error) {
        console.error('Failed to send invite:', error);
      }
    }
  };

  return (
    <div className="space-y-6 p-8 ml-72">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Team Management</h2>
          <p className="text-gray-600 mt-1">
            Manage your workspace members and permissions
          </p>
        </div>
        <motion.button
          onClick={() => setShowInviteModal(true)}
          className="bg-primary-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-primary-700 transition-colors"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <SafeIcon icon={FiPlus} />
          <span>Invite Member</span>
        </motion.button>
      </div>

      {/* Workspace Info */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-800">
              {currentWorkspace?.name || 'My Workspace'}
            </h3>
            <p className="text-gray-600 mt-1">
              {workspaceMembers.length} member{workspaceMembers.length !== 1 ? 's' : ''} • {pendingInvites.length} pending invite{pendingInvites.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <SafeIcon icon={FiUsers} className="text-gray-400" />
            <span className="text-sm text-gray-600">
              Workspace ID: {currentWorkspace?.id?.slice(-8) || 'default'}
            </span>
          </div>
        </div>
      </div>

      {/* Members List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100">
        <div className="p-6 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-gray-800">
            Members ({workspaceMembers.length})
          </h3>
        </div>
        <div className="divide-y divide-gray-100">
          {workspaceMembers.map((member, index) => (
            <motion.div
              key={member.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="p-6 flex items-center justify-between"
            >
              <div className="flex items-center space-x-4">
                <img
                  src={member.avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=3b82f6&color=fff`}
                  alt={member.name}
                  className="w-12 h-12 rounded-full"
                />
                <div>
                  <div className="flex items-center space-x-2">
                    <h4 className="font-medium text-gray-800">{member.name}</h4>
                    {member.id === user?.id && (
                      <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                        You
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">{member.email}</p>
                  <p className="text-xs text-gray-500">
                    Joined {new Date(member.joinedAt).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-3 py-1 text-sm rounded-full border ${
                  member.role === 'owner' ? 'text-yellow-600 border-yellow-600' :
                  member.role === 'admin' ? 'text-purple-600 border-purple-600' :
                  member.role === 'editor' ? 'text-blue-600 border-blue-600' :
                  'text-gray-600 border-gray-600'
                }`}>
                  {member.role}
                </span>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Team Features Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
            <SafeIcon icon={FiStar} className="text-yellow-500 mr-2" />
            Team Features
          </h3>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3 mb-2">
              <SafeIcon icon={FiShield} className="text-purple-600" />
              <h4 className="font-medium text-gray-800">Role-based Permissions</h4>
            </div>
            <p className="text-sm text-gray-600">Control access levels for team members</p>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3 mb-2">
              <SafeIcon icon={FiUsers} className="text-blue-600" />
              <h4 className="font-medium text-gray-800">Team Collaboration</h4>
            </div>
            <p className="text-sm text-gray-600">Work together on question banks and papers</p>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3 mb-2">
              <SafeIcon icon={FiSettings} className="text-green-600" />
              <h4 className="font-medium text-gray-800">Workspace Management</h4>
            </div>
            <p className="text-sm text-gray-600">Organize and manage shared resources</p>
          </div>
          <div className="p-4 border border-gray-200 rounded-lg">
            <div className="flex items-center space-x-3 mb-2">
              <SafeIcon icon={FiMail} className="text-orange-600" />
              <h4 className="font-medium text-gray-800">Team Invitations</h4>
            </div>
            <p className="text-sm text-gray-600">Invite colleagues to join your workspace</p>
          </div>
        </div>
      </div>

      {/* Simple Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[90] p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-lg w-full max-w-md p-6"
          >
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Invite Team Member</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="colleague@company.com"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleInvite}
                  className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                >
                  Send Invite
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default TeamManagement;